import { isJSON } from "validator";

class Storage {
  constructor() {
    if (window.appStorage) return;
    window.appStorage = this;
    this.clearExpired();
    this.clearExpiredInterval();
  }

  set({ key, data = {}, expiry, session_only = false }: { key: string, data: any, expiry?: [number, string], session_only?: boolean }) {
    let itemValue: any = {
      data: data,
    };
    if (expiry && expiry.length === 2) {
      if (typeof expiry[0] !== 'number' || typeof expiry[1] !== 'string')
        return console.error(
          'Invalid expiry value used when attempting to set item ' +
            key +
            'in local storage'
        );
      const numberValue = expiry[0];
      const unitType = expiry[1];
      let now = new Date().getTime();
      switch (unitType) {
        case 's':
        case 'secs':
        case 'second':
        case 'seconds':
          now += numberValue * 1000;
          break;
        case 'm':
        case 'mins':
        case 'minute':
        case 'minutes':
          now += numberValue * (60 * 1000);
          break;
        case 'd':
        case 'day':
        case 'days':
          now += numberValue * (24 * 60 * 60 * 1000);
          break;
        case 'w':
        case 'week':
        case 'weeks':
          now += numberValue * (7 * 24 * 60 * 60 * 1000);
          break;
        default:
          return console.error(
            'Invalid expiry unit type used when attempting to set item ' +
              key +
              'in local storage',
            expiry
          );
      }
      itemValue['expiry'] = now;
    }

    if (session_only) {
      this.retryWithFallbacks(() => {
        window.sessionStorage.setItem(key, JSON.stringify(itemValue));
      }, [ this.clearExpired, ()=> {this.clearExpired(true)}, this.clearSessionStorage]);
    } else {
      this.retryWithFallbacks(() => {
        window.localStorage.setItem(key, JSON.stringify(itemValue));
      }, [ this.clearExpired, ()=> {this.clearExpired(true)}, this.clearLocalStorage ]);
    }
  }

  get<T>({ key }: { key: string }): T | any | null {
    let item: any =
      window.sessionStorage.getItem(key) || window.localStorage.getItem(key);
    if (!item) {
      return null;
    }

    try {
      item = JSON.parse(item);
    } catch (e) {
      return item;
    }

    if (item.expiry && item.expiry < new Date().getTime()) {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
      return null;
    } else {
      return item.data;
    }
  }

  remove({ key }: { key: string }) {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  }

  reset() {
    this.clearSessionStorage();
    this.clearLocalStorage();
  }

  retryWithFallbacks(method: ()=> void, fallbacks: Array<()=>void>) {
    let attempt = 0;
    function execute() {
      try {
        method();
      } catch (e) {
        if (attempt < fallbacks.length) {
            fallbacks[attempt]();
            attempt++;
            execute();
        }
      }
    }
    execute();
  } 

  clearSessionStorage() {
    window.sessionStorage.clear();
  }

  clearLocalStorage() {
    window.localStorage.clear();
  }

  clearExpired(clearAllWithExpiry = false) {
    const now = new Date().getTime();

    const clear = (storage: globalThis.Storage) => {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key) continue;
        const item = storage.getItem(key);
        if (!item) continue;

        let parsedItem;
        const isValidJson = isJSON(item); // Check if item is valid JSON skipping primitive types or "TRUE" or "FALSE" strings
        if (!isValidJson) continue;
        try {
          parsedItem = JSON.parse(item);
        } catch (e) {
          continue; // Skip non-JSON items
        }

        if (parsedItem.expiry && parsedItem.expiry < now) {
          storage.removeItem(key);
        } if(parsedItem.expiry && clearAllWithExpiry) {
          storage.removeItem(key);
        }
      }
    };
    const storageCount = window.localStorage.length + window.sessionStorage.length;
    clear(window.localStorage);
    clear(window.sessionStorage);
  }

  clearExpiredInterval() {
    setInterval(() => {
      this.clearExpired();
    }, 60000); // Clear expired entries every 60 seconds
  }
}

window.appStorage = new Storage();
export default window.appStorage;
